import React, { useState, useEffect } from 'react';
import { useAppointmentListSlice } from '../../appointment-copy/slice';
import { Appointment } from '../../appointment-copy/slice/types';
import { formatPhoneNumber } from 'utils/format';
import { Label } from 'app/components/ui/label';
import { SmallCheckbox } from 'app/components/ui/checkbox';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "app/components/ui/collapsible";
import { howDidYouHearAboutUs, requestSource, daysOfWeek } from 'utils/constants';
import { formatUSDate } from 'utils/date';
import { Input } from 'app/components/ui/input';
import { Textarea } from 'app/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from 'app/components/ui/radio-group';
import { Button } from 'app/components/ui/button';
import { useToast } from 'app/components/ui/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { appointmentRequestSchema, type AppointmentRequestFormData } from 'app/utils/schemas';
import { usePhoneFormat } from 'app/utils/hooks/usePhoneFormat';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem2,
} from "app/components/ui/dropdown-menu";
import { Calendar as CalendarComponent } from "app/components/ui/calendar";
import { MonthYearDropdown } from 'app/components/common/Filters/MonthYearDropdown';
import { months } from 'utils/constants';

type Props = {
  isFormOpen: boolean;
  isNotesOpen: boolean;
  setIsFormOpen: (isFormOpen: boolean) => void;
  appointmentId: string;
  onAppointmentUpdate?: () => void;
  refreshTrigger?: number;
};

const AppointmentRequestForm: React.FC<Props> = ({ isFormOpen, isNotesOpen, setIsFormOpen, appointmentId, onAppointmentUpdate, refreshTrigger }) => {
  const [appointmentData, setAppointmentData] = useState<Appointment | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [requestSourceDropdownOpen, setRequestSourceDropdownOpen] = useState(false);
  const { toast } = useToast();

  const { useLazyGetAppointmentByIdQuery, useUpdateAppointmentMutation } = useAppointmentListSlice();
  const [getAppointment, { data, isLoading, isError, error, isFetching }] = useLazyGetAppointmentByIdQuery();
  const [updateAppointment, { isLoading: isUpdating }] = useUpdateAppointmentMutation();
  const [lastFetchedId, setLastFetchedId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<AppointmentRequestFormData>({
    resolver: zodResolver(appointmentRequestSchema),
    defaultValues: {
      patientType: 'new',
      preferredDays: [],
      preferredTimes: [],
      source: [],
    },
  });

  const { handlePhoneChange } = usePhoneFormat(setValue);

  const selectedPatientType = watch('patientType');
  const preferredDays = watch('preferredDays');
  const preferredTimes = watch('preferredTimes');
  const source = watch('source');
  const requestSourceValue = watch('requestSource');
  useEffect(() => {
    if (appointmentId) {
      // Reset appointment data and editing state first to show loading state
      setAppointmentData(null);
      setIsEditing(false);
      const currentAppointmentId = appointmentId;
      setLastFetchedId(currentAppointmentId);
      
      // Fetch the new appointment data and handle the promise directly
      getAppointment({ appointmentId: currentAppointmentId })
        .unwrap()
        .then((response: any) => {
          // Only update if this is still the current appointmentId
          if (currentAppointmentId === appointmentId) {
            try {
              // Handle various response structures
              let appointment: Appointment | null = null;
              
              if (response) {
                // Try response.data first (common API wrapper)
                if (response.data && typeof response.data === 'object') {
                  appointment = response.data as Appointment;
                }
                // Try direct response (if it's already the appointment object)
                else if (response._id || response.id) {
                  appointment = response as Appointment;
                }
                // Try response.data.data (nested wrapper)
                else if (response.data?.data) {
                  appointment = response.data.data as Appointment;
                }
              }
              
              if (appointment) {
                setAppointmentData(appointment);
              } else {
                console.warn('Appointment data not found in response:', response);
              }
            } catch (error) {
              console.error('Error parsing appointment response:', error);
            }
          }
        })
        .catch((err) => {
          console.error('Error fetching appointment:', err);
          // Only clear if this is still the current appointmentId
          if (currentAppointmentId === appointmentId) {
            setAppointmentData(null);
          }
        });
    } else {
      setAppointmentData(null);
      setIsEditing(false);
      setLastFetchedId(null);
    }
  }, [appointmentId, getAppointment]);

  // Fallback: also handle data from the query state (in case promise handling fails)
  useEffect(() => {
    if (data && appointmentId && lastFetchedId === appointmentId && !appointmentData) {
      // Only use this as fallback if we don't already have appointmentData
      const responseData = data as any;
      const appointment = responseData?.data || responseData;
      if (appointment && (appointment._id === appointmentId || appointment.id === appointmentId)) {
        setAppointmentData(appointment);
      }
    }
  }, [data, appointmentId, lastFetchedId, appointmentData]);

  // Refetch appointment data when refreshTrigger changes (triggered from parent after updates)
  useEffect(() => {
    if (appointmentId && refreshTrigger !== undefined && refreshTrigger > 0) {
      getAppointment({ appointmentId })
        .unwrap()
        .then((response: any) => {
          try {
            let appointment: Appointment | null = null;
            
            if (response) {
              if (response.data && typeof response.data === 'object') {
                appointment = response.data as Appointment;
              } else if (response._id || response.id) {
                appointment = response as Appointment;
              } else if (response.data?.data) {
                appointment = response.data.data as Appointment;
              }
            }
            
            if (appointment) {
              setAppointmentData(appointment);
              setIsEditing(false);
            }
          } catch (error) {
            console.error('Error parsing appointment response:', error);
          }
        })
        .catch((err) => {
          console.error('Error refetching appointment:', err);
        });
    }
  }, [refreshTrigger, appointmentId, getAppointment]);

  useEffect(() => {
    if (appointmentData) {
      setIsFormOpen(true);
    }
  }, [appointmentData]);

  // Handler functions
  const handleDayChange = (dayId: string, checked: boolean) => {
    const currentDays = preferredDays || [];
    if (checked) {
      setValue('preferredDays', [...currentDays, dayId]);
    } else {
      setValue('preferredDays', currentDays.filter(day => day !== dayId));
    }
  };

  const handleTimeChange = (timeId: string, checked: boolean) => {
    const currentTimes = preferredTimes || [];
    if (checked) {
      setValue('preferredTimes', [...currentTimes, timeId]);
    } else {
      setValue('preferredTimes', currentTimes.filter(time => time !== timeId));
    }
  };

  const handleSourceChange = (sourceId: string, checked: boolean) => {
    if (checked) {
      setValue('source', [sourceId]);
    } else {
      setValue('source', []);
    }
  };

  const handleRequestSourceChange = (sourceKey: string) => {
    setValue('requestSource', sourceKey);
    setRequestSourceDropdownOpen(false);
  };

  const handleDateChange = (day: Date) => {
    setSelectedDate(day);
    setIsCalendarOpen(false);
  };

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i);
  const calendarMonth = new Date(selectedYear, selectedMonth, 1);

  const handleFormSubmit = (formData: AppointmentRequestFormData) => {
    const formatDateToLocalISO = (date: Date): string => {
      const now = new Date();
      const combinedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds(),
        now.getMilliseconds()
      );
      return combinedDate.toISOString();
    };

    const payload = {
      patientType: formData.patientType,
      firstName: formData.firstName,
      lastName: formData.lastName,
      preferredDays: formData.preferredDays,
      preferredTimes: formData.preferredTimes,
      servicesInterested: formData.servicesInterested,
      message: formData.message,
      source: formData.source,
      ...(formData.phone && { phoneNumber: formData.phone }),
      ...(formData.email && { email: formData.email }),
      ...(selectedDate && { date: formatDateToLocalISO(selectedDate) }),
      ...(formData.requestSource && { requestSource: formData.requestSource }),
    };

    updateAppointment({ appointmentId, body: payload }).unwrap().then(() => {
      toast({
        title: 'Appointment updated successfully',
        description: 'The appointment has been updated.',
      });
      setIsEditing(false);
      onAppointmentUpdate?.();
      // Refetch the updated appointment data and update state
      getAppointment({ appointmentId })
        .unwrap()
        .then((response: any) => {
          try {
            let appointment: Appointment | null = null;
            
            if (response) {
              if (response.data && typeof response.data === 'object') {
                appointment = response.data as Appointment;
              } else if (response._id || response.id) {
                appointment = response as Appointment;
              } else if (response.data?.data) {
                appointment = response.data.data as Appointment;
              }
            }
            
            if (appointment) {
              setAppointmentData(appointment);
            }
          } catch (error) {
            console.error('Error parsing appointment response after update:', error);
          }
        })
        .catch((err) => {
          console.error('Error refetching appointment after update:', err);
        });
    }).catch((err) => {
      toast({
        title: 'Failed to update appointment',
        description: err?.messages || 'Failed to update appointment',
        variant: 'destructive',
      });
    });
  };

  const handleEdit = () => {
    if (appointmentData) {
      console.log('appointmentData', appointmentData);
      // Populate form with current data
      setValue('patientType', appointmentData.patientType || 'new');
      setValue('firstName', appointmentData.firstName || '');
      setValue('lastName', appointmentData.lastName || '');
      setValue('phone', appointmentData.phoneNumber || '');
      setValue('email', appointmentData.email || '');
      setValue('preferredDays', appointmentData.preferredDays || []);
      setValue('preferredTimes', appointmentData.preferredTimes || []);
      setValue('servicesInterested', appointmentData.servicesInterested || '');
      setValue('message', appointmentData.message || '');
      setValue('requestSource', appointmentData.requestSource ? String(appointmentData.requestSource) : '');

      if (appointmentData.source) {
        if (Array.isArray(appointmentData.source)) {
          setValue('source', appointmentData.source);
        } else {
          setValue('source', [appointmentData.source]);
        }
      }

      // Only set selectedDate if there's an explicit date value
      if (appointmentData.date) {
        setSelectedDate(new Date(appointmentData.date));
      } else if (appointmentData.createdAt) {
        setSelectedDate(new Date(appointmentData.createdAt));
      } else {
        setSelectedDate(undefined);
      }
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedDate(undefined);
    reset();
  };

  // Check if a checkbox should be checked based on the source
  const isSourceSelected = (sourceId: string) => {
    if (!appointmentData?.source) return false;

    // Handle both string and array formats
    if (Array.isArray(appointmentData.source)) {
      return appointmentData.source.some(source =>
        source.toLowerCase() === sourceId.toLowerCase()
      );
    }

    // Handle string format
    if (typeof appointmentData.source === 'string') {
      return appointmentData.source.toLowerCase() === sourceId.toLowerCase();
    }

    return false;
  };

  return (
    <Collapsible
      open={isFormOpen}
      onOpenChange={setIsFormOpen}
      className="overflow-hidden bg-white rounded-lg shadow-[0_1px_4px_0_rgba(0,0,0,0.15)]"
    >
      <div className="flex items-center justify-between bg-[#DDE1E6] p-4 rounded-t-lg">
        <h2 className="font-serif text-[#21272A] text-base font-semibold leading-[1.2rem]">
          Appointment Request Form
        </h2>
        <div className="flex items-center gap-5">
          {appointmentData && !isEditing && (
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 rounded-[8px] bg-[#5B73F1] hover:bg-[#4A5FE0] transition-colors"
              onClick={handleEdit}
            >
              <img src="/images/edits.svg" alt="Edit" className="size-3" />
            </button>
          )}
          {isEditing && (
            <div className="flex items-center gap-[0.92rem]">
              <Button
                type="button"
                onClick={handleCancel}
                variant="ghost"
                className="text-[#697077] hover:text-[#374151] w-[4.125rem] h-[1.875rem] py-2 px-3 rounded-[0.5rem] bg-[#FAFAFA] text-xs font-normal leading-[0.9375rem] tracking-[-0.0075rem]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={handleSubmit(handleFormSubmit)}
                variant="primary"
                disabled={isUpdating}
                className="w-[3.3125rem] h-[1.875rem] py-2 px-3 rounded-[0.5rem] text-xs font-normal leading-[0.9375rem] tracking-[-0.0075rem]"
              >
                Save
              </Button>
            </div>
          )}
          <CollapsibleTrigger asChild>
            <button
              className="inline-flex items-center justify-center bg-white hover:bg-[#FAFAFA] size-5 rounded"
              tabIndex={0}
              aria-label={isFormOpen ? "Collapse" : "Expand"}
            >
              <img
                src="/images/chevron.svg"
                alt=""
                className={`transition-transform duration-200 ${isFormOpen ? '' : 'rotate-180'}`}
              />
            </button>
          </CollapsibleTrigger>
        </div>
      </div>
      <CollapsibleContent
        className={`custom-scrollbar overflow-y-auto transition-all duration-100 ease-in-out max-h-[calc(100vh-24.3125rem)] ${isFormOpen && isNotesOpen
            ? 'h-[calc(100vh-35.575rem)]'
            : isFormOpen
              ? 'h-[calc(100vh - 24.3125rem)]'
              : ''
          }`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-sm font-normal leading-[1.225rem]">
            <span className="text-[#697077]">Loading appointment data...</span>
          </div>
        ) : !appointmentData ? (
          <div className="flex items-center justify-center h-full text-sm font-normal leading-[1.225rem]">
            <span className="text-[#697077]">No appointment selected.</span>
          </div>
        ) : (
          <>
            {/* Request Date and Request Source */}
            <div className="grid grid-cols-2 gap-6 px-4 py-3 border-b border-[#DDE1E6]">
              <div className="flex gap-3 items-center">
                <p className="text-[#697077] text-xs font-light leading-[1.05rem]">
                  Request Date
                </p>
                {isEditing ? (
                  <DropdownMenu open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex gap-3 items-center focus:outline-none"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        {selectedDate ? (
                          <span className="inline-block text-[#343A3F] text-sm font-normal leading-[1.0625rem]">
                            {formatUSDate(selectedDate)}
                          </span>
                        ) : (
                          <img src="/images/calendar-icon.svg" alt="Calendar" className="size-4" />
                        )}
                        <img src="/images/chevron-down.svg" alt="Chevron" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="p-0 bg-white border border-[#DDE1E6] rounded-lg shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
                      sideOffset={4}
                      align="start"
                    >
                      <div className="relative">
                        <div className="flex absolute top-4 right-4 z-10 gap-4 justify-end items-center">
                          <MonthYearDropdown
                            selectedValue={months[selectedMonth]}
                            setSelectedValue={(value) => {
                              setSelectedMonth(months.indexOf(value as string));
                              return true;
                            }}
                            options={months}
                            type="month"
                          />
                          <MonthYearDropdown
                            selectedValue={selectedYear}
                            setSelectedValue={(value) => setSelectedYear(value as number)}
                            options={years}
                            type="year"
                          />
                        </div>
                        <CalendarComponent
                          mode="single"
                          month={calendarMonth}
                          selected={selectedDate}
                          onDayClick={(day) => handleDateChange(day)}
                          className="bg-white"
                          classNames={{
                            nav: "hidden",
                            caption_label: "hidden",
                          }}
                          disabled={(date) => date > new Date()}
                        />
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                    {appointmentData?.date ? formatUSDate(appointmentData?.date) : appointmentData?.createdAt ? formatUSDate(appointmentData?.createdAt) : "-"}
                  </p>
                )}
              </div>
              <div className="flex gap-3 items-center">
                <p className="text-[#697077] text-xs font-light leading-[1.05rem]">
                  Request Source
                </p>
                {isEditing ? (
                  <DropdownMenu open={requestSourceDropdownOpen} onOpenChange={setRequestSourceDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                      <div className="flex items-center justify-between bg-white hover:bg-[#FAFAFA] w-40 h-[1.875rem] px-6 py-3 border rounded-md text-[#343A3F] text-sm font-normal leading-[1.05rem] cursor-pointer border-[#DDE1E6] focus:ring-1 focus:ring-blue-200">
                        <span className="truncate text-[#343A3F] text-xs font-normal leading-[1.05rem]">
                          {requestSourceValue ? requestSource[requestSourceValue as unknown as keyof typeof requestSource] : 'Select request'}
                        </span>
                        <img
                          src="/images/chevron-down.svg"
                          className={`transition-transform duration-300 ease-in-out ${requestSourceDropdownOpen ? 'rotate-180' : ''}`}
                          alt="Chevron"
                        />
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="max-h-60 min-w-[9.8125rem] p-0">
                      {Object.entries(requestSource).map(([key, label]) => (
                        <DropdownMenuCheckboxItem2
                          key={key}
                          checked={requestSourceValue === key}
                          onCheckedChange={() => handleRequestSourceChange(key)}
                          className={`px-6 py-2 rounded text-sm font-normal leading-[1.05rem] tracking-[-0.00875rem] cursor-pointer ${requestSourceValue === key ? 'bg-[#5B73F1] focus:bg-[#5B73F1] text-white focus:text-[#FAFAFA]' : 'text-[#343A3F]'
                            }`}
                          onSelect={(e) => e.preventDefault()}
                        >
                          {label}
                        </DropdownMenuCheckboxItem2>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                    {requestSource[appointmentData?.requestSource as unknown as keyof typeof requestSource] || '-'}
                  </p>
                )}
              </div>
            </div>
            {/* Patient Type */}
            <div className="flex items-center gap-12 px-4 py-3 border-b border-[#DDE1E6]">
              {isEditing ? (
                <RadioGroup
                  value={selectedPatientType}
                  onValueChange={(value) => setValue('patientType', value as 'new' | 'existing')}
                  className="flex flex-row gap-12 items-center"
                >
                  <div className="flex gap-1 items-center">
                    <RadioGroupItem value="new" id="new" className="size-3 border-[#A2A9B0]" />
                    <Label htmlFor="new" className="text-[#343A3F] text-sm font-normal leading-normal mb-0 cursor-pointer">
                      New
                    </Label>
                  </div>
                  <div className="flex gap-1 items-center">
                    <RadioGroupItem value="existing" id="existing" className="size-3 border-[#A2A9B0]" />
                    <Label htmlFor="existing" className="text-[#343A3F] text-sm font-normal leading-normal mb-0 cursor-pointer">
                      Existing
                    </Label>
                  </div>
                </RadioGroup>
              ) : (
                ['new', 'existing'].map((type) => (
                  <div key={type} className="flex gap-1 items-center">
                    <span className={`inline-block size-2 rounded-full ${appointmentData.patientType === type ? 'bg-[#55B494]' : 'bg-[#F2F4F8]'
                      }`} />
                    <span className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                      {type === 'new' ? 'New' : 'Existing'}
                    </span>
                  </div>
                ))
              )}
            </div>
            {/* Name, Phone, Email */}
            <div className="grid grid-cols-2 gap-5 px-4 py-3 border-b border-[#DDE1E6]">
              <div>
                <Label htmlFor="firstName" className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1">
                  First Name
                </Label>
                {isEditing ? (
                  <Input
                    id="firstName"
                    type="text"
                    className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.firstName ? 'border-red-500' : ''}`}
                    placeholder="|"
                    {...register('firstName')}
                  />
                ) : (
                  <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                    {appointmentData.firstName || '-'}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="lastName" className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1">
                  Last Name
                </Label>
                {isEditing ? (
                  <Input
                    id="lastName"
                    type="text"
                    className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.lastName ? 'border-red-500' : ''}`}
                    placeholder="|"
                    {...register('lastName')}
                  />
                ) : (
                  <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                    {appointmentData.lastName || '-'}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="phone" className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1">
                  Phone
                </Label>
                {isEditing ? (
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.phone ? 'border-red-500' : ''}`}
                    placeholder="|"
                    {...register('phone')}
                    onChange={handlePhoneChange}
                  />
                ) : (
                  <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                    {formatPhoneNumber(appointmentData.phoneNumber) || '-'}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="email" className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1">
                  Email
                </Label>
                {isEditing ? (
                  <Input
                    id="email"
                    type="email"
                    className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="|"
                    {...register('email')}
                  />
                ) : (
                  <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                    {appointmentData.email || '-'}
                  </p>
                )}
              </div>
            </div>
            {/* Preferred Days and Times */}
            {isEditing ? (
              <>
                {/* Preferred Days - Edit Mode: Separate Line */}
                <div className="px-4 py-3 border-b border-[#DDE1E6]">
                  <h6 className="text-[#4D5358] text-xs font-light leading-[1.05rem] mb-2">
                    Preferred Appointment Day(s)
                  </h6>
                  <div className="flex gap-3 items-center">
                    {daysOfWeek.map((item) => (
                      <div key={item.id} className="flex gap-1 items-center">
                        <SmallCheckbox
                          id={item.id}
                          className="size-3 border-[0.6px] border-[#343A3F] rounded-[0.0625rem] shadow-none"
                          checked={preferredDays?.includes(item.id) || false}
                          onCheckedChange={(checked) => handleDayChange(item.id, checked as boolean)}
                        />
                        <Label htmlFor={item.id} className="text-[#343A3F] text-sm font-normal leading-[1.05rem] tracking-[-0.00875rem] mb-0 cursor-default">
                          {item.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Preferred Times - Edit Mode: Separate Line */}
                <div className="px-4 py-3 border-b border-[#DDE1E6]">
                  <h6 className="text-[#4D5358] text-xs font-light leading-[1.05rem] mb-2">
                    Preferred Appointment Time
                  </h6>
                  <div className="flex gap-3 items-center">
                    <div className="flex gap-1 items-center">
                      <SmallCheckbox
                        id="am"
                        className="size-3 border-[0.6px] border-[#343A3F] rounded-[0.0625rem] shadow-none"
                        checked={preferredTimes?.includes('AM') || false}
                        onCheckedChange={(checked) => handleTimeChange('AM', checked as boolean)}
                      />
                      <Label htmlFor="am" className="text-[#343A3F] text-sm font-normal leading-[1.05rem] tracking-[-0.00875rem] mb-0 cursor-default">
                        AM
                      </Label>
                    </div>
                    <div className="flex gap-1 items-center">
                      <SmallCheckbox
                        id="pm"
                        className="size-3 border-[0.6px] border-[#343A3F] rounded-[0.0625rem] shadow-none"
                        checked={preferredTimes?.includes('PM') || false}
                        onCheckedChange={(checked) => handleTimeChange('PM', checked as boolean)}
                      />
                      <Label htmlFor="pm" className="text-[#343A3F] text-sm font-normal leading-[1.05rem] tracking-[-0.00875rem] mb-0 cursor-default">
                        PM
                      </Label>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* View Mode: Side by Side */
              <div className="grid grid-cols-2 gap-6 px-4 py-3 border-b border-[#DDE1E6]">
                <div className="space-y-1">
                  <h6 className="text-[#697077] text-xs font-light leading-[1.05rem]">
                    Preferred Appointment Day(s)
                  </h6>
                  <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                    {appointmentData.preferredDays?.join(', ') || '-'}
                  </p>
                </div>
                <div className="space-y-1">
                  <h6 className="text-[#697077] text-xs font-light leading-[1.05rem]">
                    Preferred Appointment Time
                  </h6>
                  <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                    {appointmentData.preferredTimes?.length ? appointmentData.preferredTimes.map(time => time.toUpperCase()).join(', ') : '-'}
                  </p>
                </div>
              </div>
            )}
            {/* Services Interested */}
            <div className="px-4 py-3 border-b border-[#DDE1E6]">
              <div>
                <Label htmlFor="servicesInterested" className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1">
                  Services Interested
                </Label>
                {isEditing ? (
                  <Input
                    id="servicesInterested"
                    type="text"
                    className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.servicesInterested ? 'border-red-500' : ''}`}
                    placeholder="|"
                    {...register('servicesInterested')}
                  />
                ) : (
                  <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                    {appointmentData.servicesInterested || '-'}
                  </p>
                )}
              </div>
            </div>
            {/* Selected Promotion(s) */}
            <div className="px-4 py-3 border-b border-[#DDE1E6]">
              <div className="space-y-1">
                <h6 className="text-[#697077] text-xs font-light leading-[1.05rem]">
                  Selected Promotion(s)
                </h6>
                <div className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                  {Array.isArray(appointmentData.promotions) && appointmentData.promotions?.length > 0 ? (
                    appointmentData.promotions?.map((promotion) => (
                      <div className="space-y-2">
                        {/* Title */}
                        <div
                          dangerouslySetInnerHTML={{
                            __html: (() => {
                              let title = promotion?.title || '-';
                              if (title !== '-') {
                                // First remove bold formatting temporarily
                                title = title.replace(/\*\*(.*?)\*\*/g, '$1');
                                // Then remove various numbering patterns from the beginning
                                title = title.replace(/^#\d+\s*/, ''); // #1, #2, etc.
                                title = title.replace(/^\d+\.\s*/, ''); // 1., 2., etc.
                                title = title.replace(/^\d+\s*/, ''); // 1, 2, etc.
                                // Finally apply bold formatting back
                                title = title.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                              }
                              return title;
                            })()
                          }}
                        />
                        {/* Description with bullet points */}
                        {false && promotion?.description && (
                          <div className="ml-4">
                            <ul className="list-disc space-y-1">
                              {promotion?.description
                                .split('\n')
                                .filter(line => line.trim()) // Show all non-empty lines
                                .map((line, index) => (
                                  <li
                                    key={index}
                                    dangerouslySetInnerHTML={{
                                      __html: line
                                        .replace(/^\s*-\s*/, '') // Remove space and dash if present
                                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Convert **text** to <strong>text</strong>
                                    }}
                                  />
                                ))
                              }
                            </ul>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    '-'
                  )}
                </div>
              </div>
            </div>
            {/* Message */}
            <div className="px-4 py-3 border-b border-[#DDE1E6]">
              <div>
                <Label htmlFor="message" className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1">
                  Message
                </Label>
                {isEditing ? (
                  <Textarea
                    id="message"
                    className="bg-white min-h-[6.125rem] max-h-[6.125rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 resize-none"
                    placeholder="|"
                    {...register('message')}
                  />
                ) : (
                  <p className="text-[#21272A] text-sm font-normal leading-[1.225rem]">
                    {appointmentData.message || '-'}
                  </p>
                )}
              </div>
            </div>
            {/* How did you hear about us? */}
            {appointmentData.patientType === 'new' && (
              <div className="px-4 py-3">
                <h6 className="text-[#4D5358] text-xs font-light leading-[1.05rem] mb-2">
                  How did patient hear about us?
                </h6>
                {isEditing ? (
                  <div className="flex flex-wrap gap-y-2 gap-x-[2.875rem] items-center">
                    {howDidYouHearAboutUs.map((item) => (
                      <div key={item.id} className="flex gap-2 items-center">
                        <SmallCheckbox
                          id={item.id}
                          className="size-[0.6875rem] border-[0.6px] border-[#343A3F] rounded-[0.125rem] shadow-none"
                          checked={source?.includes(item.id) || false}
                          onCheckedChange={(checked) => handleSourceChange(item.id, checked as boolean)}
                        />
                        <Label htmlFor={item.id} className="text-[#343A3F] text-sm font-normal leading-normal mb-0 cursor-default">
                          {item.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-y-2 gap-x-8 justify-between items-start">
                    {howDidYouHearAboutUs.map((item) => (
                      <div key={item.id} className={`flex gap-2 items-center ${item.id === 'friends family' || item.id === 'doctor referral' ? 'w-[8.75rem]' : item.id === 'google' || item.id === 'others' ? 'w-[4.5rem]' : 'w-[6.6875rem]'}`}>
                        <SmallCheckbox
                          id={item.id}
                          checked={isSourceSelected(item.id)}
                          disabled={true}
                          className="size-[0.6875rem] border-[0.6px] border-[#343A3F] rounded-[0.125rem] shadow-none"
                        />
                        <Label htmlFor={item.id} className="text-[#343A3F] text-sm font-normal leading-normal mb-0 cursor-default">
                          {item.label}
                        </Label>
                      </div>
                    ))}
                    <div className="invisible w-[6.6875rem]" />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default AppointmentRequestForm;
